import React from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { groupReviewsByLanguageThenDate } from "./utils";
import { LocationReviewSummary } from "../../../../models/LocationReviewSummary";
import { ColorGenerator } from "../../../FacilityDetailsComponents/LocationKeywordsChart/colors";

import "./styles.scss";

interface ITimelineSectionProps {
  reviewSummary: LocationReviewSummary;
  chartContainerId?: string;
}

export const TimelineSection: React.FunctionComponent<ITimelineSectionProps> = (
  props
) => {
  const { reviewSummary, chartContainerId } = props;

  const { t: _t } = useTranslation();
  const t = (key: string, options?: any) =>
    _t(
      `facilityReviewSummariesPage.extendedReviewSummary.timelineSection.${key}`,
      options
    );
  const tLanguage = (key: string) => _t(`common.language.${key}`);

  // Parse the data into a format we can use to populate the chart
  const { groupedReviews, languageTotals } =
    groupReviewsByLanguageThenDate(reviewSummary);

  // Color generator for lines in the chart
  const colorGenerator = new ColorGenerator();

  // Only include languages that have at least one review
  // This list is used to populate the Legend and the Lines of the chart
  const languages: string[] = [...reviewSummary.languages].filter(
    (language) => languageTotals[language] > 0
  );

  // Add "total" as the first element in array, if there are multiple languages
  if (languages.length > 1) {
    languages.unshift("total");
  }

  return (
    <div className="white-border-panel section review-summary-timeline">
      <span className="section-title">{t("timeline")}</span>
      <div id={chartContainerId} className="timeline-chart-container">
        <LineChart width={1000} height={300} data={groupedReviews}>
          <CartesianGrid strokeDasharray="3 2" />
          <XAxis
            dataKey="review_date"
            tick={{ fontSize: 13 }}
            style={{ fontFamily: "Inter" }}
          />
          <YAxis tick={{ fontSize: 13 }} style={{ fontFamily: "Inter" }} />
          <Legend
            iconType="plainline"
            wrapperStyle={{ fontSize: "12px", fontWeight: 700 }}
          />
          <Tooltip
            itemStyle={{
              paddingTop: 0,
              paddingBottom: 0,
              marginLeft: 0,
              fontSize: "14px",
            }}
            formatter={(value) =>
              new Intl.NumberFormat("en").format(value as number)
            }
            labelFormatter={(value) => {
              return `${t("date")}: ${value}`;
            }}
          />
          {languages.map((language) => (
            <React.Fragment key={language}>
              <Line
                type="linear"
                dataKey={`languages.${language}`}
                name={tLanguage(language)}
                dot={false}
                stroke={colorGenerator.getColorForLabel(language)}
                strokeWidth={6}
              />
            </React.Fragment>
          ))}
        </LineChart>
      </div>
    </div>
  );
};
